import React, { useState } from 'react';

import { Accordion, Avatar, Box, BoxProps, Button, Center, Group, Loader, Text } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { YoutubeVideoComponent } from 'components/BatchResourceSelector/YoutubeVideoSelector/YoutubeVideo';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { YoutubeService } from 'services/youtube-service';
import { YoutubeChannel, YoutubeChannelVideosResult, YoutubeVideo } from 'shared/interfaces/Youtube';

interface YoutubeChannelProps extends BoxProps {
  channel: YoutubeChannel;
  isVideoSelected: (videoId: string) => boolean;
  onVideoSelect: (youtubeVideo: YoutubeVideo) => void;
}

export function YoutubeChannelComponent({ channel, isVideoSelected, onVideoSelect, ...boxProps }: YoutubeChannelProps) {
  const [channelVideos, setChannelVideos] = useState<YoutubeVideo[]>([]);
  const [channelVideosIds, setChannelVideosIds] = useState<Set<string>>(new Set());
  const [lastChannelVideosResult, setLastChannelVideosResult] = useState<YoutubeChannelVideosResult>();
  const { t } = useTranslation();

  const channelVideosMutation = useMutation({
    mutationFn: YoutubeService.getChannelVideos,
    onSuccess: (channelVideosResult: YoutubeChannelVideosResult) => {
      setLastChannelVideosResult(channelVideosResult);
      const filteredVideos = channelVideosResult.videos.filter((video) => !channelVideosIds.has(video.id));
      setChannelVideos([...channelVideos, ...filteredVideos]);
      setChannelVideosIds(new Set([...channelVideosIds, ...filteredVideos.map((video) => video.id)]));
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.YOUTUBE.CHANNEL_VIDEOS.FAILURE')
  });

  const loadMoreChannelVideos = () => {
    if (!lastChannelVideosResult?.nextPageToken) return;
    channelVideosMutation.mutate({
      channelId: channel.id,
      nextPageToken: lastChannelVideosResult.nextPageToken
    });
  };

  const loadFirstChannelVideos = (value: string | null) => {
    if (!value || channelVideos.length > 0) return;
    channelVideosMutation.mutate({
      channelId: channel.id
    });
  };
  return (
    <Accordion
      chevronPosition="right"
      variant="contained"
      mt={10}
      mb={10}
      onChange={(value) => loadFirstChannelVideos(value)}
      {...boxProps}>
      <Accordion.Item value={channel.id}>
        <Accordion.Control>
          <Group noWrap>
            <Avatar src={channel.imageUrl} radius="xl" size="lg" />
            <Box>
              <Text lineClamp={1}>{channel.title}</Text>
              <Text lineClamp={2}>{channel.description}</Text>
            </Box>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {channelVideos.map((video, key: number) => {
            return (
              <YoutubeVideoComponent
                key={key}
                video={video}
                onVideoSelect={onVideoSelect}
                isSelected={isVideoSelected(video.id)}
              />
            );
          })}
          {channelVideos.length === 0 && channelVideosMutation.isError && (
            <Center mt={10} mb={10} h={100}>
              <IconExclamationCircle color="red" />
            </Center>
          )}
          {channelVideos.length === 0 && channelVideosMutation.isLoading && !channelVideosMutation.isError && (
            <Center mt={10} mb={10} h={100}>
              <Loader variant="dots" />
            </Center>
          )}
          {channelVideos.length !== 0 && lastChannelVideosResult?.nextPageToken && (
            <Center mt={10} mb={10}>
              <Button loading={channelVideosMutation.isLoading} onClick={() => loadMoreChannelVideos()}>
                {t('APP.ACTIONS.LOAD_MORE')}
              </Button>
            </Center>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
